import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FilterListIcon from '@mui/icons-material/FilterList';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router';
import {
    fetchRequirementClassifications,
    searchRequirementsList,
} from '../../firebase/firestore/icr';
import {coverageScoreColor, coverageTrackColor} from './coverage/coverageScoreColor';
import GapPriorityCell from './components/GapPriorityCell';
import PriorityIndicator from './components/PriorityIndicator';
import AuthorChip from '../products/components/common/AuthorChip';
import OverlapBadges from './similarity/OverlapBadges';
import {resolveGapNavPath, TYPE_COLORS} from './similarity/similarityLabels';
import {
    buildRequirementsListSearch,
    isDefaultStatusForTab,
    requirementsListReturnTo,
} from './requirementsListUrlState';
import RequirementsOwnerTagsFilters from './RequirementsOwnerTagsFilters';

/**
 * Shared header + body column tracks (one parent grid + subgrid rows).
 * Separate per-row grids mis-align: header labels expand tracks via min-content
 * while row cells stay narrow. Subgrid keeps every row on the same lines.
 */
const DESKTOP_GRID = [
    'minmax(0, 56px)',      // Priority
    'minmax(0, 1.9fr)',     // Title / Question
    'minmax(0, 112px)',     // ID (fixed — ICR-2026-XXXXX)
    'minmax(0, 0.75fr)',    // Author
    'minmax(0, 118px)',     // Data Coverage
    'minmax(0, 132px)',     // Analytic Coverage
    'minmax(0, 92px)',      // Due date
    'minmax(0, 72px)',      // Similar
].join(' ');
const DESKTOP_GAP = 1.5;
/** Horizontal inset on the shared desktop grid; header bg full-bleeds past this. */
const DESKTOP_PX = 1.5;

const COLUMNS = [
    // Backend "question" sort is priority-rank first — expose that on the Priority header.
    {key: 'priority', label: 'Priority', sortKey: 'question'},
    {key: 'question', label: 'Question', sortable: false},
    {key: 'number', label: 'ID'},
    {key: 'author', label: 'Author'},
    {key: 'dataCoverage', label: 'Data Coverage'},
    {key: 'analyticCoverage', label: 'Analytic Coverage'},
    {key: 'dueDate', label: 'Due date'},
    {key: 'overlap', label: 'Similar'},
];

const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];
const STATUS_OPTIONS = [
    {value: 'active', label: 'Active'},
    {value: 'expired', label: 'Expired'},
    {value: 'proposed', label: 'Proposed'},
];
const GAP_STATUS_OPTIONS = [
    {value: 'identified', label: 'Identified'},
    {value: 'proposed', label: 'Proposed'},
    {value: 'promoted', label: 'Promoted to EEI'},
];
/** Priority filter sentinel for unset ("?") gaps. */
const PRIORITY_UNSET = 'unset';

function coverageColor(pct) {
    if (pct == null) {
        return {track: '#eeeeee', fill: '#bdbdbd', label: '#9e9e9e'};
    }
    return {
        track: coverageTrackColor(pct),
        fill: coverageScoreColor(pct),
        label: coverageScoreColor(pct),
    };
}

/** Single-metric coverage cell (Data or Analytic) with a thin percentage bar. */
function CoveragePctCell({pct, title}) {
    const {track, fill, label} = coverageColor(pct);
    const fillWidth = pct == null ? 0 : Math.max(Math.min(pct, 100), pct > 0 ? 2 : 0);
    return (
        <Tooltip title={pct != null ? `${title} ${pct}%` : `No ${title.toLowerCase()}`} arrow>
            <Stack spacing={0.35} sx={{minWidth: 0, width: '100%', maxWidth: '100%'}}>
                <Typography
                    variant="caption"
                    fontSize="0.62rem"
                    fontWeight={700}
                    fontFamily='"Roboto Mono", monospace'
                    sx={{color: label}}
                >
                    {pct != null ? `${pct}%` : '—'}
                </Typography>
                <Box sx={{width: '100%', height: 3, bgcolor: track, borderRadius: 1, overflow: 'hidden'}}>
                    {fillWidth > 0 && (
                        <Box sx={{width: `${fillWidth}%`, height: '100%', bgcolor: fill}}/>
                    )}
                </Box>
            </Stack>
        </Tooltip>
    );
}

function DueDateCell({dueDate, status, na = false}) {
    if (na) {
        return <Typography variant="body2" color="text.secondary">N/A</Typography>;
    }
    if (!dueDate) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    const parsed = dayjs(dueDate);
    if (!parsed.isValid()) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    return (
        <Typography
            variant="caption"
            fontWeight={600}
            fontFamily='"Roboto Mono", monospace'
            sx={{color: status === 'expired' ? 'error.main' : 'text.secondary'}}
        >
            {parsed.format('YYYY-MM-DD')}
        </Typography>
    );
}

function AuthorCell({item}) {
    const userId = item.authorUserId || null;
    const displayName = item.authorDisplayName || item.author || null;
    if (!userId && !displayName) {
        return (
            <Typography variant="body2" color="text.secondary">—</Typography>
        );
    }
    return (
        <Box
            sx={{
                minWidth: 0,
                // Requirements list: show full name (no ellipsis), wrap if needed
                '& > .MuiStack-root': {minWidth: 0, alignItems: 'flex-start'},
                '& .MuiLink-root, & span.MuiTypography-root, & a, & span': {
                    whiteSpace: 'normal !important',
                    overflow: 'visible !important',
                    textOverflow: 'clip !important',
                    wordBreak: 'break-word',
                    lineHeight: 1.25,
                },
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <AuthorChip
                userId={userId}
                userDisplayName={displayName || userId || 'Unknown'}
                flex={undefined}
                avatarProfile={item.authorProfile || null}
            />
        </Box>
    );
}

function TypeBadge({type}) {
    const label = type === 'EEI' ? 'EEI' : type === 'GAP' ? 'GAP' : 'ICR';
    const color = TYPE_COLORS[label] || '#757575';
    return (
        <Chip
            size="small"
            label={label}
            aria-label={label}
            sx={{
                height: 18,
                mt: 0.15,
                flexShrink: 0,
                fontWeight: 700,
                fontSize: '0.65rem',
                bgcolor: `${color}22`,
                color,
                '& .MuiChip-label': {px: 0.6},
            }}
        />
    );
}

function PriorityCell({item, isGap}) {
    if (isGap) {
        return <GapPriorityCell priority={item.priority}/>;
    }
    return (
        <Box sx={{display: 'flex', pt: 0.15, minWidth: 0}}>
            <PriorityIndicator
                priority={item.priority}
                showLabel={false}
                sx={{height: 18, width: 20, flexShrink: 0}}
            />
        </Box>
    );
}

/** Full-bleed hover/focus highlight past parent grid px (matches header ::before). */
const rowHoverBleedBefore = {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: (theme) => theme.spacing(-DESKTOP_PX),
    right: (theme) => theme.spacing(-DESKTOP_PX),
    bgcolor: 'transparent',
    pointerEvents: 'none',
    zIndex: 0,
};

const rowInteractiveDesktopSx = {
    cursor: 'pointer',
    position: 'relative',
    alignSelf: 'stretch',
    '& > *': {position: 'relative', zIndex: 1},
    '&::before': rowHoverBleedBefore,
    '&:hover::before': {bgcolor: 'action.hover'},
};

const rowInteractiveMobileSx = {
    cursor: 'pointer',
    '&:hover': {bgcolor: 'action.hover'},
};

function rowKeyDown(onOpen, item) {
    return (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(item);
        }
    };
}

function QuestionCell({item, showPriority = false, isGap = false}) {
    return (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{minWidth: 0}}>
            {showPriority && (
                <Box sx={{flexShrink: 0}}>
                    {isGap ? (
                        <GapPriorityCell priority={item.priority}/>
                    ) : (
                        <PriorityIndicator
                            priority={item.priority}
                            showLabel={false}
                            sx={{height: 18, width: 20, mt: 0.15}}
                        />
                    )}
                </Box>
            )}
            <TypeBadge type={item.type}/>
            <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    lineHeight: 1.35,
                    minWidth: 0,
                }}
            >
                {item.question || '—'}
            </Typography>
        </Stack>
    );
}

/** Desktop row: subgrid child spanning the shared parent column tracks. */
function RequirementRowDesktop({item, onOpen, isGap}) {
    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={() => onOpen(item)}
            onKeyDown={rowKeyDown(onOpen, item)}
            sx={{
                gridColumn: '1 / -1',
                display: 'grid',
                gridTemplateColumns: 'subgrid',
                alignItems: 'start',
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                ...rowInteractiveDesktopSx,
                '&:last-child': {borderBottom: 0},
            }}
        >
            <PriorityCell item={item} isGap={isGap}/>
            <Box sx={{minWidth: 0, overflow: 'hidden'}}>
                <QuestionCell item={item}/>
            </Box>
            <Box sx={{minWidth: 0, overflow: 'hidden'}}>
                <Typography
                    variant="caption"
                    fontWeight={700}
                    fontFamily='"Roboto Mono", monospace'
                    color="text.secondary"
                    sx={{overflowWrap: 'anywhere'}}
                >
                    {item.number || '—'}
                </Typography>
            </Box>
            <Box sx={{minWidth: 0, overflow: 'hidden'}}>
                <AuthorCell item={item}/>
            </Box>
            <Box sx={{minWidth: 0}}>
                <CoveragePctCell pct={item.dataCoveragePct} title="Data coverage"/>
            </Box>
            <Box sx={{minWidth: 0}}>
                <CoveragePctCell pct={item.analyticCoveragePct} title="Analytic coverage"/>
            </Box>
            <Box sx={{minWidth: 0}}>
                <DueDateCell dueDate={item.dueDate} status={item.status} na={isGap}/>
            </Box>
            <Box sx={{minWidth: 0}}>
                <OverlapBadges overlap={item.overlap}/>
            </Box>
        </Box>
    );
}

/** Mobile stacked card row (single column). */
function RequirementRowMobile({item, onOpen, isGap}) {
    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={() => onOpen(item)}
            onKeyDown={rowKeyDown(onOpen, item)}
            sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
                ...rowInteractiveMobileSx,
                '&:last-child': {borderBottom: 0},
            }}
        >
            <Stack spacing={0.75} sx={{minWidth: 0}}>
                <QuestionCell
                    item={item}
                    showPriority
                    isGap={isGap}
                />
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center'}}>
                    <Typography
                        variant="caption"
                        fontWeight={700}
                        fontFamily='"Roboto Mono", monospace'
                        color="text.secondary"
                    >
                        {item.number || '—'}
                    </Typography>
                    <AuthorCell item={item}/>
                    <CoveragePctCell pct={item.dataCoveragePct} title="Data coverage"/>
                    <CoveragePctCell pct={item.analyticCoveragePct} title="Analytic coverage"/>
                    <DueDateCell dueDate={item.dueDate} status={item.status} na={isGap}/>
                    <OverlapBadges overlap={item.overlap}/>
                </Box>
            </Stack>
        </Box>
    );
}

function ColumnHeader({column, sortBy, sortDir, onSort}) {
    const apiSortKey = column.sortKey || column.key;
    const sortable = column.sortable !== false;
    const active = sortable && sortBy === apiSortKey;
    const labelSx = {
        minWidth: 0,
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
    };

    if (!sortable) {
        return (
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={labelSx}>
                {column.label}
            </Typography>
        );
    }

    return (
        <Box
            component="button"
            type="button"
            onClick={() => onSort(apiSortKey)}
            sx={{
                display: 'inline-flex',
                alignItems: 'flex-start',
                gap: 0.25,
                p: 0,
                m: 0,
                width: '100%',
                height: '100%',
                minWidth: 0,
                border: 0,
                background: 'none',
                cursor: 'pointer',
                userSelect: 'none',
                textAlign: 'left',
                // Keep pointer over label/icon (browsers otherwise show the text I-beam).
                '& *': {cursor: 'pointer'},
            }}
        >
            <Typography
                variant="caption"
                fontWeight={700}
                color={active ? 'primary.main' : 'text.secondary'}
                sx={labelSx}
            >
                {column.label}
            </Typography>
            {active && (
                sortDir === 'asc'
                    ? <ArrowUpwardIcon sx={{fontSize: 12, color: 'primary.main', flexShrink: 0, mt: '1px'}}/>
                    : <ArrowDownwardIcon sx={{fontSize: 12, color: 'primary.main', flexShrink: 0, mt: '1px'}}/>
            )}
        </Box>
    );
}

/**
 * ICR / EEI / Gap searchable table with full filter panel.
 * Owner + Tags are controlled by the parent so Overview shares the same filters.
 */
export default function RequirementsCollectionPanel({
    entityType,
    searchQ,
    onSearchQChange,
    filterTags,
    onFilterTagsChange,
    filterOwners,
    onFilterOwnersChange,
    filterPriority,
    onFilterPriorityChange,
    filterClassification,
    onFilterClassificationChange,
    filterStatus,
    onFilterStatusChange,
    filtersOpen,
    onFiltersOpenChange,
    sortBy,
    sortDir,
    onSortChange,
    onTotalChange,
}) {
    const navigate = useNavigate();
    const isGap = entityType === 'gap';
    const [debouncedQ, setDebouncedQ] = useState(searchQ);
    const [classificationOptions, setClassificationOptions] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const statusOptions = isGap ? GAP_STATUS_OPTIONS : STATUS_OPTIONS;

    const filtersActive = Boolean(
        filterTags.length
        || filterOwners.length
        || filterPriority
        || (!isGap && filterClassification)
        || !isDefaultStatusForTab(entityType, filterStatus),
    );

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(searchQ), 250);
        return () => clearTimeout(t);
    }, [searchQ]);

    useEffect(() => {
        if (isGap) return undefined;
        let cancelled = false;
        fetchRequirementClassifications()
            .then((values) => {
                if (!cancelled) setClassificationOptions(Array.isArray(values) ? values : []);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [isGap]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await searchRequirementsList({
                entityType,
                q: debouncedQ,
                tags: filterTags,
                owners: filterOwners,
                priority: filterPriority || undefined,
                classification: isGap ? undefined : (filterClassification || undefined),
                status: filterStatus.length ? filterStatus : undefined,
                sortBy: sortBy || undefined,
                sortDir: sortBy ? sortDir : undefined,
                page: 0,
                size: 200,
            });
            const nextTotal = data?.total ?? 0;
            setItems(data?.items || []);
            onTotalChange?.(nextTotal);
        } catch (e) {
            setError(e?.message || 'Failed to load requirements');
            setItems([]);
            onTotalChange?.(0);
        } finally {
            setLoading(false);
        }
    }, [
        entityType, isGap, debouncedQ, filterTags, filterOwners, filterPriority,
        filterClassification, filterStatus, sortBy, sortDir, onTotalChange,
    ]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSort = useCallback((columnKey) => {
        onSortChange((prev) => (prev.by === columnKey
            ? {by: columnKey, dir: prev.dir === 'asc' ? 'desc' : 'asc'}
            : {by: columnKey, dir: 'asc'}));
    }, [onSortChange]);

    const columns = useMemo(
        () => COLUMNS.map((column) => (
            column.key === 'question'
                ? {...column, label: entityType === 'icr' ? 'Title' : 'Question'}
                : column
        )),
        [entityType],
    );

    const searchPlaceholder = isGap
        ? 'Search gap questions, GAP numbers…'
        : 'Search ICR and EEI questions, numbers…';

    const openItem = (item) => {
        if (!item) return;
        const returnTo = requirementsListReturnTo(buildRequirementsListSearch({
            tab: entityType,
            q: searchQ,
            tags: filterTags,
            owners: filterOwners,
            priority: filterPriority,
            classification: isGap ? '' : filterClassification,
            status: filterStatus,
            sortBy,
            sortDir,
            filtersOpen,
        }));
        const navOpts = {state: {returnTo}};
        if (item.type === 'GAP') {
            const path = resolveGapNavPath({
                type: 'GAP',
                number: item.number,
                id: item.id,
            });
            if (path) navigate(path, navOpts);
            return;
        }
        if (item.type === 'EEI') {
            navigate(`/icr/eei/${encodeURIComponent(item.number || item.id)}`, navOpts);
            return;
        }
        navigate(`/icr/${encodeURIComponent(item.number || item.id)}`, navOpts);
    };

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                    size="small"
                    fullWidth
                    placeholder={searchPlaceholder}
                    value={searchQ}
                    onChange={(e) => onSearchQChange(e.target.value)}
                />
                <IconButton
                    size="small"
                    aria-label={filtersOpen ? 'Hide filters' : 'Show filters'}
                    aria-expanded={filtersOpen}
                    color={filtersOpen || filtersActive ? 'primary' : 'default'}
                    onClick={() => onFiltersOpenChange((open) => !open)}
                    sx={{mt: 0.25, flexShrink: 0}}
                >
                    <FilterListIcon/>
                </IconButton>
            </Stack>
            {filtersOpen && (
                <Stack spacing={1.5}>
                    <RequirementsOwnerTagsFilters
                        owners={filterOwners}
                        tags={filterTags}
                        onOwnersChange={onFilterOwnersChange}
                        onTagsChange={onFilterTagsChange}
                        ownerLabel="Author"
                        entityType={entityType}
                    />
                    <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="requirements-priority-label">Priority</InputLabel>
                            <Select
                                labelId="requirements-priority-label"
                                label="Priority"
                                value={filterPriority}
                                onChange={(e) => onFilterPriorityChange(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {PRIORITY_OPTIONS.map((p) => (
                                    <MenuItem key={p} value={p}>{p}</MenuItem>
                                ))}
                                {isGap && (
                                    <MenuItem value={PRIORITY_UNSET}>N/A</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        {!isGap && (
                            <FormControl size="small" fullWidth>
                                <InputLabel id="requirements-classification-label">Classification</InputLabel>
                                <Select
                                    labelId="requirements-classification-label"
                                    label="Classification"
                                    value={filterClassification}
                                    onChange={(e) => onFilterClassificationChange(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {classificationOptions.map((c) => (
                                        <MenuItem key={c} value={c}>{c}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Autocomplete
                            multiple
                            size="small"
                            fullWidth
                            options={statusOptions.map((s) => s.value)}
                            value={filterStatus}
                            filterSelectedOptions
                            disableCloseOnSelect
                            getOptionLabel={(value) => (
                                statusOptions.find((s) => s.value === value)?.label || value
                            )}
                            onChange={(_, value) => onFilterStatusChange(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={isGap ? 'Gap status' : 'Status'}
                                    placeholder={isGap ? 'Add gap status…' : 'Add status…'}
                                />
                            )}
                        />
                    </Stack>
                </Stack>
            )}

            {loading && items.length === 0 && <LinearProgress/>}
            {error && (
                <Typography variant="body2" color="error">{error}</Typography>
            )}

            {!loading && !error && items.length === 0 && (
                <Box py={4}>
                    <Typography color="text.secondary" align="center">
                        {entityType === 'eei'
                            ? 'No EEIs match these filters.'
                            : entityType === 'gap'
                                ? 'No Gaps match these filters.'
                                : 'No ICRs match these filters.'}
                    </Typography>
                </Box>
            )}

            {items.length > 0 && (
                <Box
                    sx={{
                        position: 'relative',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Box
                        aria-busy={loading || undefined}
                        sx={{
                            ...(loading && {
                                pointerEvents: 'none',
                                userSelect: 'none',
                                opacity: 0.55,
                            }),
                        }}
                    >
                        {/* One shared grid so header + rows share identical column tracks. */}
                        <Box
                            sx={{
                                display: {xs: 'none', md: 'grid'},
                                gridTemplateColumns: DESKTOP_GRID,
                                columnGap: DESKTOP_GAP,
                                alignItems: 'start',
                                px: DESKTOP_PX,
                            }}
                        >
                            <Box
                                sx={{
                                    gridColumn: '1 / -1',
                                    display: 'grid',
                                    gridTemplateColumns: 'subgrid',
                                    alignItems: 'start',
                                    position: 'relative',
                                    py: 0.75,
                                    // Full-bleed past parent px so header bg reaches table edges.
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        bottom: 0,
                                        left: (theme) => theme.spacing(-DESKTOP_PX),
                                        right: (theme) => theme.spacing(-DESKTOP_PX),
                                        bgcolor: 'action.hover',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        pointerEvents: 'none',
                                    },
                                    '& > *': {position: 'relative'},
                                }}
                            >
                                {columns.map((column) => (
                                    <Box
                                        key={column.key}
                                        sx={{
                                            minWidth: 0,
                                            height: '100%',
                                            ...(column.sortable !== false && {cursor: 'pointer'}),
                                        }}
                                    >
                                        <ColumnHeader
                                            column={column}
                                            sortBy={sortBy}
                                            sortDir={sortDir}
                                            onSort={handleSort}
                                        />
                                    </Box>
                                ))}
                            </Box>
                            {items.map((item) => (
                                <RequirementRowDesktop
                                    key={`${item.type}:${item.id}`}
                                    item={item}
                                    onOpen={openItem}
                                    isGap={isGap}
                                />
                            ))}
                        </Box>
                        <Box sx={{display: {xs: 'block', md: 'none'}}}>
                            {items.map((item) => (
                                <RequirementRowMobile
                                    key={`${item.type}:${item.id}`}
                                    item={item}
                                    onOpen={openItem}
                                    isGap={isGap}
                                />
                            ))}
                        </Box>
                    </Box>
                    {loading && (
                        <Box
                            data-testid="requirements-table-loading-overlay"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(255, 255, 255, 0.45)',
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress size={32}/>
                        </Box>
                    )}
                </Box>
            )}
        </Stack>
    );
}
